import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Image as ImageIcon, Check, FileText, BookOpen, Sparkles, Bike, Download } from "lucide-react";
import api, { openPdf } from "../../lib/api";
import SignRestoContract from "../../components/SignRestoContract";
import SignHandoverContract from "../../components/SignHandoverContract";
import RestaurateurQRCode from "../../components/RestaurateurQRCode";

// Onglet "Profil établissement" — description + galerie photos (Object Storage)
export default function ProfileTab({ resto, onRestoUpdated }) {
  const { t, i18n } = useTranslation();
  const [description, setDescription] = useState(resto?.description || "");
  const [cuisine, setCuisine] = useState(resto?.cuisine || "");
  const [phone, setPhone] = useState(resto?.phone || "");
  const [openingHoursText, setOpeningHoursText] = useState(resto?.opening_hours_text || "");
  const [deliverySlotsText, setDeliverySlotsText] = useState(resto?.delivery_slots_text || "");
  const [minOrder, setMinOrder] = useState(resto?.min_order_eur ?? "");
  const [deliveryTime, setDeliveryTime] = useState(resto?.delivery_time_min ?? "");
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState(resto?.gallery || []);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUrl, setCoverUrl] = useState(resto?.cover_url || resto?.logo_url || "");
  const [aiCoverBusy, setAiCoverBusy] = useState(false);
  const [aiStyle, setAiStyle] = useState("réaliste");
  const [commission, setCommission] = useState(resto?.commission_rate_pct || 25);
  const [savingCommission, setSavingCommission] = useState(false);

  const saveCommission = async (rate) => {
    setSavingCommission(true);
    try {
      await api.post("/restaurateur/settings/commission", { commission_rate_pct: rate });
      setCommission(rate);
      toast.success(`Taux de commission mis à jour : ${rate} %`);
      onRestoUpdated && onRestoUpdated({ ...resto, commission_rate_pct: rate });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur.");
    } finally { setSavingCommission(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/restaurateur/profile", {
        description: description.trim(),
        cuisine: cuisine.trim(),
        phone: phone.trim(),
        opening_hours_text: openingHoursText.trim(),
        delivery_slots_text: deliverySlotsText.trim(),
        min_order_eur: minOrder === "" || minOrder === null ? 0 : Number(minOrder),
        delivery_time_min: deliveryTime === "" || deliveryTime === null ? 0 : Number(deliveryTime),
      });
      toast.success(t("restaurateur.profile.saved"));
      onRestoUpdated && onRestoUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur.");
    } finally { setSaving(false); }
  };

  const onChangeCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo trop lourde (max 8 Mo)."); e.target.value = ""; return; }
    setCoverUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
      });
      const { data } = await api.patch("/restaurateur/profile", { photo_data_url: dataUrl });
      setCoverUrl(data.cover_url || data.logo_url || "");
      onRestoUpdated && onRestoUpdated(data);
      toast.success("Photo principale mise à jour ✅ — vous pouvez publier votre vitrine.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur upload.");
    } finally { setCoverUploading(false); e.target.value = ""; }
  };

  const onAddPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Upload multi-photos séquentiel : chaque fichier est ajouté à la galerie l'un après l'autre.
    // Séquentiel (pas parallèle) pour éviter les race conditions côté serveur sur le champ `gallery`
    // et pour respecter les quotas d'upload (8 Mo/fichier, 20 max au total).
    setUploading(true);
    let added = 0, skipped = 0;
    try {
      for (const f of files) {
        // Vérifie le quota AU FIL DE L'EAU car chaque insert change gallery.length côté serveur.
        // On lit la longueur courante via `setGallery(prev => prev)` pattern — mais plus simple :
        // on utilise added + gallery.length de départ.
        if (gallery.length + added >= 20) {
          skipped = files.length - added;
          toast.warning(`Galerie pleine (max 20) — ${skipped} photo(s) ignorée(s).`);
          break;
        }
        if (f.size > 8 * 1024 * 1024) {
          skipped += 1;
          toast.error(`${f.name} : ${t("restaurateur.menu.err.tooBig")}`);
          continue;
        }
        try {
          const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
          });
          const { data } = await api.post("/restaurateur/gallery", {
            photo_data_url: dataUrl,
            caption: caption.trim(),
          });
          setGallery((s) => [...s, data]);
          added += 1;
        } catch (err) {
          skipped += 1;
          toast.error(`${f.name} : ${err?.response?.data?.detail || "erreur upload"}`);
        }
      }
      if (added > 0) {
        setCaption("");
        toast.success(`${added} photo${added > 1 ? "s" : ""} ajoutée${added > 1 ? "s" : ""} ✅`);
      }
    } finally { setUploading(false); e.target.value = ""; }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm(t("restaurateur.profile.confirmRemovePhoto"))) return;
    try {
      await api.delete(`/restaurateur/gallery/${photoId}`);
      setGallery((s) => s.filter((g) => g.id !== photoId));
      toast.success(t("restaurateur.profile.removePhoto") + " ✅");
    } catch { toast.error("Erreur."); }
  };

  return (
    <div data-testid="rt-profile-tab" className="space-y-8">
      <section>
        <h2 className="font-head font-black text-2xl text-[#1A1A1A]">{t("restaurateur.profile.title")}</h2>
        <p className="text-sm text-[#75807A] mt-1">{t("restaurateur.profile.subtitle")}</p>

        <form onSubmit={saveProfile} className="mt-5 bg-white rounded-3xl border border-[#E2DFD8] p-6 space-y-4">
          <label className="block">
            <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.profile.field.description")}</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder={t("restaurateur.profile.ph.description")}
              rows={5} maxLength={1500} data-testid="rt-profile-desc"
              className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none"
            />
            <div className="text-[11px] text-[#75807A] mt-1">{description.length}/1500</div>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.profile.field.cuisine")}</span>
              <input type="text" value={cuisine} onChange={(e) => setCuisine(e.target.value)} maxLength={80} data-testid="rt-profile-cuisine"
                className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-[#1A1A1A]">{t("restaurateur.profile.field.phone")}</span>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} data-testid="rt-profile-phone"
                className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-semibold text-[#1A1A1A]">Horaires d'ouverture</span>
            <textarea value={openingHoursText} onChange={(e) => setOpeningHoursText(e.target.value)} maxLength={400} rows={2} data-testid="rt-profile-hours"
              placeholder="Ex: Lun-Ven 11h-22h · Sam-Dim 12h-23h · Fermé le mardi"
              className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm resize-none" />
            <div className="text-[11px] text-[#75807A] mt-1">{openingHoursText.length}/400 · Affiché aux clients dans la fiche "Infos restaurant"</div>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-[#1A1A1A]">Créneaux de livraison planifiée</span>
            <textarea value={deliverySlotsText} onChange={(e) => setDeliverySlotsText(e.target.value)} maxLength={400} rows={2} data-testid="rt-profile-slots"
              placeholder="Ex: 11:30, 12:00, 12:30, 13:00, 13:30, 14:00, 18:30, 19:00, 19:30, 20:00, 20:30, 21:00"
              className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none text-sm resize-none" />
            <div className="text-[11px] text-[#75807A] mt-1">{deliverySlotsText.length}/400 · Créneaux proposés aux clients qui commandent en mode « Planifier pour plus tard » (format HH:MM séparés par des virgules)</div>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-[#1A1A1A]">Minimum de commande (€)</span>
            <input type="number" step="0.50" min="0" value={minOrder} onChange={(e) => setMinOrder(e.target.value)}
              data-testid="rt-profile-min-order" placeholder="Laisser vide = pas de minimum"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            <div className="text-[11px] text-[#75807A] mt-1">Sous-total minimum requis pour commander (hors frais de livraison). Ex: 15 €. Laisser vide ou 0 = pas de minimum.</div>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-[#1A1A1A]">Temps de livraison estimé (min)</span>
            <input type="number" step="5" min="5" max="180" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)}
              data-testid="rt-profile-delivery-time" placeholder="Ex: 30"
              className="mt-1 w-full h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            <div className="text-[11px] text-[#75807A] mt-1">Estimation affichée aux clients sur votre vitrine (préparation + trajet). Entre 5 et 180 min. Laisser vide = non affiché.</div>
          </label>
          <div>
            <button type="submit" disabled={saving} data-testid="rt-profile-save"
              className="h-11 px-6 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("common.save")}
            </button>
          </div>
        </form>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">Taux de commission Nold Go</h3>
            <p className="text-sm text-[#75807A]">Part prélevée par Nold Go sur le sous-total (hors livraison) de chaque commande.</p>
          </div>
        </div>
        <div className="bg-white rounded-3xl border-2 border-[#16a34a]/30 p-6 flex items-center gap-4 flex-wrap" data-testid="rt-commission-block">
          <span className="text-4xl font-head font-black text-[#16a34a]">{commission} %</span>
          <div className="flex gap-2 flex-wrap">
            {[15, 20, 25, 30].map((r) => (
              <button key={r} type="button" data-testid={`rt-commission-${r}`}
                disabled={savingCommission || r === commission}
                onClick={() => saveCommission(r)}
                className={`h-10 px-4 rounded-full font-bold text-sm transition-colors ${r === commission ? "bg-[#16a34a] text-white cursor-default" : "bg-white border-2 border-[#16a34a] text-[#16a34a] hover:bg-[#F0FDF4]"}`}>
                {r} %
              </button>
            ))}
          </div>
          <p className="w-full text-xs text-[#75807A] mt-1">
            Exemple pour une commande de 20 € (hors livraison) à {commission} % : vous touchez <b>{(20 * (100 - commission) / 100).toFixed(2)} €</b>, Nold Go prélève <b>{(20 * commission / 100).toFixed(2)} €</b>.
          </p>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">Photo principale de votre vitrine</h3>
            <p className="text-sm text-[#75807A]">Cette photo apparaît en bandeau sur noldgo.com. Obligatoire pour publier votre vitrine.</p>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="w-full sm:w-64 shrink-0 aspect-video rounded-2xl bg-[#F0EBE1] overflow-hidden border border-[#E2DFD8]" data-testid="rt-cover-preview">
              {coverUrl ? (
                <img src={coverUrl} alt="Photo principale" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-[#75807A]">
                  <ImageIcon className="w-10 h-10 opacity-60" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm cursor-pointer ${coverUploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="rt-cover-upload-btn">
                {coverUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                {coverUrl ? "Changer la photo" : "Ajouter la photo principale"}
                <input type="file" accept="image/*" onChange={onChangeCover} className="hidden" disabled={coverUploading} data-testid="rt-cover-input" />
              </label>
              {/* Photo Auto AI — Gemini Nano Banana. Génère un bandeau sur-mesure avec choix du style. */}
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={aiStyle}
                  onChange={(e) => setAiStyle(e.target.value)}
                  disabled={aiCoverBusy}
                  data-testid="rt-cover-ai-style"
                  className="h-11 px-3 rounded-full bg-white border-2 border-[#7C3AED]/30 text-sm font-semibold text-[#4C1D95] focus:border-[#7C3AED] disabled:opacity-60"
                >
                  <option value="réaliste">Réaliste</option>
                  <option value="moderne">Moderne</option>
                  <option value="rustique">Rustique</option>
                  <option value="cosy">Cosy</option>
                  <option value="street food">Street food</option>
                </select>
                <button
                  type="button"
                  onClick={async () => {
                    if (aiCoverBusy) return;
                    if (!window.confirm(`Générer une photo bandeau via IA (style : ${aiStyle}) ? Cela remplace la photo actuelle. Génération 10-20 secondes.`)) return;
                    setAiCoverBusy(true);
                    try {
                      const { data } = await api.post("/restaurateur/generate-cover-ai", { style: aiStyle });
                      setCoverUrl(data.cover_url || "");
                      onRestoUpdated && onRestoUpdated(data.restaurant);
                      toast.success(`🎨 Photo IA "${aiStyle}" appliquée !`);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Génération échouée.");
                    } finally { setAiCoverBusy(false); }
                  }}
                  disabled={aiCoverBusy}
                  data-testid="rt-cover-ai-btn"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#4C1D95] hover:from-[#6D28D9] hover:to-[#3F1181] text-white font-bold text-sm disabled:opacity-60 disabled:cursor-not-allowed shadow-md"
                >
                  {aiCoverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {aiCoverBusy ? "Génération en cours…" : "🎨 Générer par IA"}
                </button>
              </div>
              <p className="text-xs text-[#75807A] mt-2">JPG, PNG ou WEBP · 8 Mo max · idéalement 1600×900px.</p>
            </div>
          </div>
        </div>
      </section>

      {/* QR Code de la vitrine — scan direct vers le menu du resto (pas la liste) */}
      <section>
        <div className="mb-3">
          <h3 className="font-head font-black text-xl text-[#1A1A1A]">QR Code — Marketing vitrine</h3>
          <p className="text-sm text-[#75807A]">
            À imprimer sur la vitrine, la table, un flyer. Le client scanne → arrive directement sur votre menu.
          </p>
        </div>
        <RestaurateurQRCode restaurantId={resto?.id} restaurantName={resto?.name} />
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">{t("restaurateur.profile.galleryTitle")}</h3>
            <p className="text-sm text-[#75807A]">{t("restaurateur.profile.gallerySubtitle")}</p>
          </div>
          <div className="text-xs text-[#75807A] font-semibold">{gallery.length} photo{gallery.length > 1 ? "s" : ""}</div>
        </div>

        <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={120}
              placeholder={t("restaurateur.profile.caption")} data-testid="rt-gallery-caption"
              className="flex-1 h-11 px-3 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none" />
            <label className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="rt-gallery-add-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t("restaurateur.profile.addPhoto")}
              <input type="file" accept="image/*" multiple onChange={onAddPhoto} className="hidden" disabled={uploading} data-testid="rt-gallery-input" />
            </label>
          </div>

          {gallery.length === 0 ? (
            <div className="py-10 text-center">
              <ImageIcon className="w-10 h-10 text-[#F26419] mx-auto opacity-60" />
              <div className="mt-3 text-sm text-[#75807A]">{t("restaurateur.profile.galleryEmpty")}</div>
            </div>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((g) => (
                <li key={g.id} className="relative group rounded-xl overflow-hidden bg-[#F0EBE1] aspect-square" data-testid={`rt-gallery-${g.id}`}>
                  <img src={g.url} alt={g.caption || ""} className="w-full h-full object-cover" loading="lazy" />
                  {g.caption && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-white text-xs font-medium">{g.caption}</div>}
                  <button onClick={() => removePhoto(g.id)} data-testid={`rt-gallery-remove-${g.id}`}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 hover:bg-[#D32F2F] hover:text-white grid place-items-center transition-colors opacity-0 group-hover:opacity-100">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Signature électronique du contrat de partenariat + QR de vérification publique */}
      <section>
        <div className="mb-3">
          <h3 className="font-head font-black text-xl text-[#1A1A1A]">
            {i18n.language === "nl" ? "Elektronische ondertekening van het contract" : "Signature électronique du contrat"}
          </h3>
          <p className="text-sm text-[#75807A]">
            {i18n.language === "nl"
              ? "Onderteken online — geen scanner of afdruk nodig. QR-code voor authenticiteit."
              : "Signez en ligne — sans scanner ni impression. QR pour prouver l'authenticité."}
          </p>
        </div>
        <SignRestoContract lang={i18n.language === "nl" ? "nl" : "fr"} />
        <SignHandoverContract lang={i18n.language === "nl" ? "nl" : "fr"} />
      </section>

      {/* Pack Administratif Nold Go — téléchargement PDF */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">{t("restaurateur.docs.title")}</h3>
            <p className="text-sm text-[#75807A]">{t("restaurateur.docs.subtitle")}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DocCard kind="contract" icon={FileText} title={t("restaurateur.docs.contract.title")} desc={t("restaurateur.docs.contract.desc")} />
          <DocCard kind="manual-resto" icon={BookOpen} title={t("restaurateur.docs.manualResto.title")} desc={t("restaurateur.docs.manualResto.desc")} />
          <DocCard kind="manual-livreur" icon={Bike} title={t("restaurateur.docs.manualLivreur.title")} desc={t("restaurateur.docs.manualLivreur.desc")} />
          <DocCard kind="brochure" icon={Sparkles} title={t("restaurateur.docs.brochure.title")} desc={t("restaurateur.docs.brochure.desc")} />
        </div>
      </section>
    </div>
  );
}

function DocCard({ kind, icon: Icon, title, desc }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      await openPdf(`/restaurateur/documents/${kind}.pdf`, `nold-go-${kind}.pdf`);
    } catch { toast.error(t("restaurateur.docs.error")); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={download} disabled={loading} data-testid={`rt-doc-${kind}`}
      className="text-left rounded-2xl bg-white border border-[#E2DFD8] hover:border-[#F26419] p-4 flex items-start gap-3 transition-colors disabled:opacity-60">
      <div className="w-11 h-11 rounded-xl bg-[#FFF3E6] grid place-items-center text-[#F26419] shrink-0"><Icon className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="font-head font-bold text-[#1A1A1A]">{title}</div>
        <div className="text-xs text-[#75807A] mt-0.5">{desc}</div>
      </div>
      <div className="shrink-0 mt-1">{loading ? <Loader2 className="w-4 h-4 animate-spin text-[#F26419]" /> : <Download className="w-4 h-4 text-[#F26419]" />}</div>
    </button>
  );
}
